import { type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { errorReason } from '../../lib/api';
import type { TerminalStatus } from '../../lib/terminal-status';
import {
  keysTitle,
  keysTone,
  pendingTitle,
  pendingTone,
  pendingValue,
  prsTitle,
  queueTitle,
  retriesTitle,
  statusTitle,
  statusTone,
  statusWord,
  type TerminalStatusCell,
  vmsTitle,
  workersRunning,
  workersTitle,
} from '../../lib/terminal-status-view';

// The Terminal window's status line (#4395, epic #4391): STATUS · WORKERS ·
// KEYS · VMS · QUEUE · PRS (since the 14:00 UTC standup cutoff, the same day
// window as Home's merged tile) · PENDING · RETRIES/hr, mono, tabular-nums,
// each cell a click-to-filter toggle over the log pane's own rows (D-C: the
// SAME server functions Fleet's utilization card and the overview strip use,
// never a client re-derivation of them). The per-cell VALUES are computed in
// `lib/terminal-status-view.ts` (pure, testable without a render) — this file
// is the render loop and the filter-toggle wiring only.
//
// CLICK-TO-FILTER. A cell narrows the FOCUSED pane to one `subject.kind`
// (`STATUS_CELL_KIND`), a client-side filter over rows already held. PRESENTER
// ONLY: which cell is pressed is the focused pane's own state
// (`use-terminal-panes.ts`'s `statusCellFor`), passed in as `activeCell`, so a
// focus switch shows each pane's own pressed cell (plan 101 slice 19 row 19-14).
// Keyed on the CELL, not the kind: `queue`/`pending`/`retries` share `'task'`,
// and comparing by kind lit all three at once (#4395 review).

function StatusCell(props: {
  readonly label: string;
  readonly value: string;
  readonly tone?: 'warning' | 'danger' | undefined;
  readonly title: string;
  readonly active: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      class="terminal__cell"
      classList={{ 'terminal__cell--active': props.active }}
      title={props.title}
      aria-pressed={props.active}
      onClick={props.onClick}
    >
      <span class="terminal__cell-label">{props.label}</span>
      <span
        class="terminal__cell-value"
        classList={{
          [`terminal__cell-value--${props.tone}`]: props.tone !== undefined,
        }}
      >
        {props.value}
      </span>
    </button>
  );
}

export function TerminalStatusLine(props: {
  readonly status: TerminalStatus | undefined;
  /** The read's failure, rendered only while no snapshot is held (suspected 19-S8). */
  readonly error: unknown;
  readonly activeCell: TerminalStatusCell | null;
  readonly onToggle: (cell: TerminalStatusCell) => void;
}): JSX.Element {
  const isActive = (cell: TerminalStatusCell): boolean => props.activeCell === cell;
  const toggle = (cell: TerminalStatusCell): void => props.onToggle(cell);
  // The failure line, empty while a snapshot is held or the first read is pending.
  const failure = (): string =>
    props.status === undefined && props.error
      ? t('terminal.status_unavailable', { reason: errorReason(props.error, t('errors.generic')) })
      : '';

  return (
    <>
      {/* ALWAYS MOUNTED, empty until a read fails: many screen readers speak only
          a CHANGE inside a live region that already exists, so a region mounted
          with its text already in it may never be announced. The same node
          switches from `sr-only` to the visible line, never a second element. */}
      <p class={failure() === '' ? 'sr-only' : 'terminal__statusline'} role="status">
        <Show when={failure()}>
          {(text) => (
            <span class="terminal__cell-value terminal__cell-value--danger">{text()}</span>
          )}
        </Show>
      </p>
      <Show when={props.status}>
        {(status) => (
          <div class="terminal__statusline">
            <StatusCell
              label={t('terminal.cell_status')}
              value={statusWord(status())}
              tone={statusTone(status())}
              title={statusTitle(status())}
              active={isActive('status')}
              onClick={() => toggle('status')}
            />
            <StatusCell
              label={t('terminal.cell_workers')}
              value={String(workersRunning(status()))}
              title={workersTitle(status())}
              active={isActive('workers')}
              onClick={() => toggle('workers')}
            />
            <StatusCell
              label={t('terminal.cell_keys')}
              value={String(status().keys.usable)}
              tone={keysTone(status())}
              title={keysTitle(status())}
              active={isActive('keys')}
              onClick={() => toggle('keys')}
            />
            <StatusCell
              label={t('terminal.cell_vms')}
              value={`${status().workers.online}/${status().workers.total}`}
              title={vmsTitle(status())}
              active={isActive('vms')}
              onClick={() => toggle('vms')}
            />
            <StatusCell
              label={t('terminal.cell_queue')}
              value={String(status().queue.depth)}
              title={queueTitle(status())}
              active={isActive('queue')}
              onClick={() => toggle('queue')}
            />
            <StatusCell
              label={t('terminal.cell_prs_24h')}
              value={`${status().prs_24h.merged}/${status().prs_24h.opened}`}
              title={prsTitle(status())}
              active={isActive('prs_24h')}
              onClick={() => toggle('prs_24h')}
            />
            <StatusCell
              label={t('terminal.cell_pending')}
              value={pendingValue(status())}
              tone={pendingTone(status())}
              title={pendingTitle(status())}
              active={isActive('pending')}
              onClick={() => toggle('pending')}
            />
            <StatusCell
              label={t('terminal.cell_retries')}
              value={String(status().retries_last_hour)}
              title={retriesTitle(status())}
              active={isActive('retries')}
              onClick={() => toggle('retries')}
            />
          </div>
        )}
      </Show>
    </>
  );
}
